import React from "react";
import { useToast } from "@/hooks/use-toast";

const UPDATE_INTERVAL = 60000; // 1 minute
const VERSION_FILE_PATH = "/VERSION.txt";

export const useVersionNotifier = () => {
  const [currentVersion, setCurrentVersion] = React.useState<string | null>(null);
  const { toast } = useToast();
  const isProduction = import.meta.env.PROD;

  React.useEffect(() => {
    // Only run in production
    if (!isProduction) {
      return;
    }

    const checkVersion = async () => {
      try {
        const response = await fetch(VERSION_FILE_PATH, {
          cache: "no-cache",
        });
        const newVersion = await response.text();
        const trimmedVersion = newVersion.trim();

        if (currentVersion && trimmedVersion !== currentVersion) {
          toast({
            title: "New Version Available",
            description: (
              <div>
                We have released a new version. Please{" "}
                <button
                  onClick={() => window.location.reload()}
                  className="underline font-medium"
                >
                  refresh
                </button>{" "}
                to get the latest features.
              </div>
            ),
            duration: 20000, // 20 seconds
          });
        }

        setCurrentVersion(trimmedVersion);
      } catch (error) {
        // Silently fail - version checking is not critical
        console.warn("Failed to check version:", error);
      }
    };

    // Initial check
    checkVersion();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkVersion, UPDATE_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [currentVersion, isProduction, toast]);
};
